import { useState } from 'react';

const REGION_FLAGS = {
  DE: '🇩🇪', FR: '🇫🇷', IT: '🇮🇹', NL: '🇳🇱', PT: '🇵🇹',
  UK: '🇬🇧', ES: '🇪🇸', SE: '🇸🇪', PL: '🇵🇱',
  US: '🇺🇸', CA: '🇨🇦', MX: '🇲🇽', BR: '🇧🇷', CO: '🇨🇴', AR: '🇦🇷',
  AU: '🇦🇺', JP: '🇯🇵', SG: '🇸🇬', AE: '🇦🇪', IN: '🇮🇳',
};

const STATUS = {
  ordered:   { label: 'Ordered',   color: '#60a5fa', bg: 'rgba(96,165,250,0.15)',  dot: '#3b82f6' },
  confirmed: { label: 'Ordered',   color: '#60a5fa', bg: 'rgba(96,165,250,0.15)',  dot: '#3b82f6' },
  shipped:   { label: 'Shipped',   color: '#34d399', bg: 'rgba(52,211,153,0.15)',  dot: '#10b981' },
  delivered: { label: 'Delivered', color: '#a78bfa', bg: 'rgba(167,139,250,0.15)', dot: '#8b5cf6' },
  cancelled: { label: 'Cancelled', color: '#f87171', bg: 'rgba(248,113,113,0.15)', dot: '#ef4444' },
  returned:  { label: 'Returned',  color: '#fbbf24', bg: 'rgba(251,191,36,0.15)',  dot: '#f59e0b' },
};

const STATUS_ORDER = ['ordered', 'confirmed', 'shipped', 'delivered', 'cancelled', 'returned'];

// The journey a healthy order walks. Cancelled and returned leave it.
const PIPELINE = ['ordered', 'shipped', 'delivered'];

// How long a stage can last before it is worth looking at. An order sitting in
// "ordered" has not been dispatched; one sitting in "shipped" is lost in transit.
const STALE_DAYS = { ordered: 5, shipped: 10 };

const DAY = 86_400_000;

function norm(status) { return status === 'confirmed' ? 'ordered' : status; }
function isLive(o)    { return ['ordered', 'confirmed', 'shipped'].includes(o.status); }
function isDone(o)    { return o.status === 'delivered'; }

function daysSince(iso) {
  return (Date.now() - new Date(iso).getTime()) / DAY;
}

// An order needing a look: still moving, but stuck in its stage too long.
function staleFor(o) {
  if (!isLive(o)) return 0;
  const limit = STALE_DAYS[norm(o.status)];
  const age   = daysSince(o.emailDate);
  return limit && age > limit ? age : 0;
}

function hasIssue(o) {
  return o.status === 'cancelled' || o.status === 'returned' || staleFor(o) > 0;
}

function ageLabel(iso) {
  const h = (Date.now() - new Date(iso).getTime()) / 3_600_000;
  if (h < 1)  return `${Math.round(h * 60)}m ago`;
  if (h < 24) return `${Math.floor(h)}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

function money(o) {
  if (o.amount === null || o.amount === undefined) return null;
  const sym = { GBP: '£', USD: '$', EUR: '€' }[o.currency] || '';
  return `${sym}${Number(o.amount).toFixed(2)}`;
}

// Group on the resolved product. Everything the emails could not identify goes
// to one bucket, which is currently most of them.
function groupByProduct(orders) {
  const map = new Map();
  for (const o of orders) {
    const key = o.product?.asin || (o.product?.name ? `name:${o.product.name}` : 'unidentified');
    if (!map.has(key)) {
      map.set(key, { key, asin: o.product?.asin || null, name: o.product?.name || null,
                     imageUrl: o.product?.imageUrl || null, source: o.product?.source || null, orders: [] });
    }
    const g = map.get(key);
    if (!g.imageUrl && o.product?.imageUrl) g.imageUrl = o.product.imageUrl;
    if (!g.name     && o.product?.name)     g.name     = o.product.name;
    g.orders.push(o);
  }
  return [...map.values()].sort((a, b) => {
    // Anything identified sorts above the unidentified bucket, then by recency
    if ((a.key === 'unidentified') !== (b.key === 'unidentified')) return a.key === 'unidentified' ? 1 : -1;
    const la = Math.max(...a.orders.map(o => new Date(o.emailDate).getTime()));
    const lb = Math.max(...b.orders.map(o => new Date(o.emailDate).getTime()));
    return lb - la;
  });
}

function statusSummary(orders) {
  const counts = {};
  for (const o of orders) counts[norm(o.status)] = (counts[norm(o.status)] || 0) + 1;
  return STATUS_ORDER.filter(s => s !== 'confirmed' && counts[s]).map(s => ({ status: s, count: counts[s] }));
}

// ─── Pieces ──────────────────────────────────────────────────────────────────

function StatusPill({ status, small }) {
  const s = STATUS[status] || STATUS.ordered;
  return (
    <span style={{
      fontSize: small ? 10 : 11, fontWeight: 600, padding: small ? '2px 8px' : '3px 10px',
      borderRadius: 20, background: s.bg, color: s.color, whiteSpace: 'nowrap',
    }}>
      {s.label}
    </span>
  );
}

// Ordered → Shipped → Delivered, with the reached stages filled in. Cancelled
// and returned orders never finish it, so they show a broken track instead.
function Pipeline({ order }) {
  const cur     = norm(order.status);
  const broken  = cur === 'cancelled' || cur === 'returned';
  const reached = broken ? 1 : PIPELINE.indexOf(cur) + 1;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 3, flexShrink: 0 }} title={
      broken ? `Order ${cur}` : `Reached: ${PIPELINE.slice(0, reached).join(' → ')}`
    }>
      {PIPELINE.map((stage, i) => {
        const done = i < reached;
        const color = broken && i > 0 ? 'rgba(248,113,113,0.25)'
                    : done ? (STATUS[stage]?.dot || '#3b82f6') : 'rgba(255,255,255,0.12)';
        return (
          <span key={stage} style={{
            width: i === 0 ? 7 : 18, height: 4, borderRadius: 3, background: color,
            transition: 'background 0.2s',
          }} />
        );
      })}
    </div>
  );
}

function Tile({ label, value, sub, accent }) {
  return (
    <div style={{
      flex: 1, minWidth: 108, padding: '11px 14px', borderRadius: 10,
      background: 'rgba(0,0,0,0.22)', border: '1px solid rgba(255,255,255,0.07)',
    }}>
      <div style={{ fontSize: 10, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
        {label}
      </div>
      <div style={{ fontSize: 20, fontWeight: 700, color: accent || '#f1f5f9', marginTop: 3, letterSpacing: '-0.4px' }}>
        {value}
      </div>
      {sub && <div style={{ fontSize: 10, color: '#475569', marginTop: 1 }}>{sub}</div>}
    </div>
  );
}

function OrderRow({ order, isMobile, showProduct }) {
  const stale = staleFor(order);
  const amt   = money(order);

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: isMobile ? 8 : 12, flexWrap: 'wrap',
      padding: isMobile ? '9px 12px' : '10px 16px',
      borderTop: '1px solid rgba(255,255,255,0.04)',
      background: stale ? 'rgba(251,146,60,0.06)' : 'transparent',
    }}>
      <Pipeline order={order} />
      <StatusPill status={norm(order.status)} small />

      <span style={{
        fontFamily: '"SF Mono", "Fira Code", monospace', fontSize: 11, color: '#64748b',
        whiteSpace: 'nowrap',
      }}>
        {order.orderId || '—'}
      </span>

      {showProduct && order.product?.name && (
        <span style={{ fontSize: 11, color: '#94a3b8', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {order.product.name}
        </span>
      )}

      <span style={{
        fontSize: 11, color: '#475569', flex: 1, minWidth: 120,
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      }}>
        {order.accountEmail || '—'}
      </span>

      {order.region && (
        <span style={{ fontSize: 11, color: '#94a3b8', whiteSpace: 'nowrap' }}>
          {REGION_FLAGS[order.region] || ''} {order.region}
        </span>
      )}

      {amt && (
        <span style={{ fontSize: 12, fontWeight: 600, color: '#e2e8f0', whiteSpace: 'nowrap' }}>{amt}</span>
      )}

      {order.trackingNumber && (
        <span title="Tracking number" style={{
          fontFamily: '"SF Mono", "Fira Code", monospace', fontSize: 10, color: '#64748b',
          background: 'rgba(255,255,255,0.05)', borderRadius: 5, padding: '1px 6px', whiteSpace: 'nowrap',
        }}>
          🚚 {order.trackingNumber}
        </span>
      )}

      <span style={{ fontSize: 11, color: '#475569', whiteSpace: 'nowrap' }}>{ageLabel(order.emailDate)}</span>

      {stale > 0 && (
        <span style={{
          fontSize: 10, fontWeight: 700, color: '#fb923c',
          background: 'rgba(251,146,60,0.15)', borderRadius: 20, padding: '2px 8px', whiteSpace: 'nowrap',
        }}>
          ⚠ {norm(order.status) === 'ordered' ? 'not dispatched' : 'in transit'} {Math.floor(stale)}d
        </span>
      )}
    </div>
  );
}

function ProductCard({ group, isMobile }) {
  const [expanded, setExpanded] = useState(true);
  const [imgError, setImgError] = useState(false);

  const unidentified = group.key === 'unidentified';
  const imgSrc = group.imageUrl
    || (group.asin ? `https://images-na.ssl-images-amazon.com/images/P/${group.asin}.01.LZZZZZZZ.jpg` : null);

  const live   = group.orders.filter(isLive).length;
  const issues = group.orders.filter(hasIssue).length;
  const summary = statusSummary(group.orders);

  return (
    <div style={{
      background: 'rgba(255,255,255,0.03)',
      border: '1px solid rgba(255,255,255,0.08)',
      borderRadius: 12, overflow: 'hidden',
    }}>
      <div
        onClick={() => setExpanded(e => !e)}
        style={{ display: 'flex', gap: isMobile ? 10 : 14, padding: isMobile ? '12px' : '13px 16px', cursor: 'pointer', alignItems: 'center' }}
      >
        <div style={{
          width: isMobile ? 44 : 56, height: isMobile ? 44 : 56, flexShrink: 0,
          background: 'rgba(255,255,255,0.05)', borderRadius: 10,
          border: '1px solid rgba(255,255,255,0.1)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden',
        }}>
          {imgSrc && !imgError
            ? <img src={imgSrc} alt={group.name || group.asin} style={{ width: '100%', height: '100%', objectFit: 'contain' }} onError={() => setImgError(true)} />
            : <span style={{ fontSize: 22 }}>{unidentified ? '❓' : '📦'}</span>}
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          {group.asin && (
            <div style={{ fontSize: 10, fontFamily: '"SF Mono", "Fira Code", monospace', color: '#475569', letterSpacing: '0.04em' }}>
              {group.asin}
            </div>
          )}
          <div style={{ fontSize: 13, fontWeight: 600, color: unidentified ? '#94a3b8' : '#f1f5f9', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {unidentified ? 'Product not identified' : (group.name || group.asin)}
          </div>
          <div style={{ display: 'flex', gap: 5, marginTop: 6, flexWrap: 'wrap', alignItems: 'center' }}>
            {summary.map(s => (
              <span key={s.status} style={{
                fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 20,
                background: STATUS[s.status].bg, color: STATUS[s.status].color,
              }}>
                {s.count} {STATUS[s.status].label}
              </span>
            ))}
            {issues > 0 && (
              <span style={{ fontSize: 10, fontWeight: 700, color: '#fb923c', background: 'rgba(251,146,60,0.15)', borderRadius: 20, padding: '2px 8px' }}>
                ⚠ {issues} need{issues === 1 ? 's' : ''} a look
              </span>
            )}
            {unidentified && (
              <span style={{ fontSize: 10, color: '#475569' }}>
                dispatch emails carry no product — links to an invite once the buying account has one
              </span>
            )}
          </div>
        </div>

        <div style={{ textAlign: 'right', flexShrink: 0 }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: live > 0 ? '#60a5fa' : '#475569', lineHeight: 1.1 }}>{live}</div>
          <div style={{ fontSize: 9, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.06em' }}>in flight</div>
        </div>
        <span style={{ fontSize: 12, color: '#334155', flexShrink: 0 }}>{expanded ? '▲' : '▼'}</span>
      </div>

      {expanded && group.orders
        .slice()
        .sort((a, b) => new Date(b.emailDate) - new Date(a.emailDate))
        .map(o => <OrderRow key={o.id} order={o} isMobile={isMobile} showProduct={unidentified} />)}
    </div>
  );
}

// ─── Main ────────────────────────────────────────────────────────────────────

export default function OrdersTable({ orders, isMobile }) {
  const [view, setView] = useState('all'); // all | live | issues

  if (!orders.length) {
    return (
      <div style={{
        textAlign: 'center', padding: '48px 0', color: '#334155', fontSize: 14,
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10,
      }}>
        <span style={{ fontSize: 32 }}>📦</span>
        No orders found — run a sync to fetch emails
      </div>
    );
  }

  const live      = orders.filter(isLive);
  const delivered = orders.filter(isDone);
  const issues    = orders.filter(hasIssue);
  const inTransit = orders.filter(o => norm(o.status) === 'shipped');

  const shown = view === 'live' ? live : view === 'issues' ? issues : orders;
  const groups = groupByProduct(shown);
  // With nothing identified there is only ever one group, and wrapping every
  // order in a single anonymous card buys nothing — show them as a flat list.
  const flat = groups.length === 1 && groups[0].key === 'unidentified';

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
        <Tile label="In flight" value={live.length} sub={`${inTransit.length} in transit`} accent="#60a5fa" />
        <Tile label="Delivered" value={delivered.length} accent="#a78bfa" />
        <Tile label="Need a look" value={issues.length}
              sub={issues.length ? 'stuck, cancelled or returned' : 'all moving'}
              accent={issues.length ? '#fb923c' : '#34d399'} />
        <Tile label="Total" value={orders.length} sub={`${groups.length} product${groups.length !== 1 ? 's' : ''}`} />
      </div>

      <div style={{ display: 'flex', gap: 4, background: 'rgba(255,255,255,0.03)', borderRadius: 10, padding: 4, marginBottom: 14, width: 'fit-content' }}>
        {[['all', `All (${orders.length})`], ['live', `In flight (${live.length})`], ['issues', `Need a look (${issues.length})`]].map(([key, label]) => (
          <button key={key} onClick={() => setView(key)} style={{
            fontSize: 11, fontWeight: 600, padding: '6px 14px', borderRadius: 8, border: 'none', cursor: 'pointer',
            background: view === key ? '#334155' : 'transparent',
            color: view === key ? '#f1f5f9' : '#64748b', whiteSpace: 'nowrap',
          }}>{label}</button>
        ))}
      </div>

      {shown.length === 0 ? (
        <div style={{ padding: '32px 0', textAlign: 'center', color: '#475569', fontSize: 13 }}>
          {view === 'issues' ? 'Nothing stuck, cancelled or returned.' : 'No orders in flight.'}
        </div>
      ) : flat ? (
        <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12, overflow: 'hidden' }}>
          <div style={{ padding: isMobile ? '10px 12px' : '10px 16px', fontSize: 11, color: '#475569' }}>
            No product could be identified for these — Amazon's dispatch emails carry an order number but no item.
            Each will attach to its invited product once the buying account has a logged invite.
          </div>
          {shown
            .slice()
            .sort((a, b) => new Date(b.emailDate) - new Date(a.emailDate))
            .map(o => <OrderRow key={o.id} order={o} isMobile={isMobile} showProduct />)}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {groups.map(g => <ProductCard key={g.key} group={g} isMobile={isMobile} />)}
        </div>
      )}
    </div>
  );
}
